import { WebSocketServer } from "ws";
import type { Server as HTTPServer } from "http";
import { join } from "path";
import { hostname as getHostname } from "node:os";
import { timingSafeEqual } from "node:crypto";
import type { ClientScope } from "./auth-server-client.js";
import type { AgentManager } from "./agent/agent-manager.js";
import type { AgentStorage } from "./agent/agent-storage.js";
import type { DownloadTokenStore } from "./file-download/token-store.js";
import type { TerminalManager } from "../terminal/terminal-manager.js";
import type pino from "pino";
import type {
  PersistedWorkspaceRecord,
  ProjectRegistry,
  WorkspaceRegistry,
} from "./workspace-registry.js";
import type { WorkspaceStacksService } from "./workspace-stacks-service.js";
import type { TodoStore } from "./todo-store.js";
import type { FileBackedChatService } from "./chat/chat-service.js";
import type { LoopService } from "./loop-service.js";
import type { QuestService } from "./quest/service.js";
import type { ScheduleService } from "./schedule/service.js";
import type { CheckoutDiffManager, CheckoutDiffMetrics } from "./checkout-diff-manager.js";
import type { DaemonConfigStore, MutableDaemonConfig } from "./daemon-config-store.js";
import {
  type ServerInfoStatusPayload,
  type SessionOutboundMessage,
  type WSHelloMessage,
  WSInboundMessageSchema,
  type ServerCapabilityState,
  type ServerCapabilities,
  type WSOutboundMessage,
  wrapSessionMessage,
} from "./messages.js";
import { asUint8Array, decodeTerminalStreamFrame } from "../shared/terminal-stream-protocol.js";
import type { HostnamesConfig } from "./hostnames.js";
import { isHostnameAllowed } from "./hostnames.js";
import {
  Session,
  type SessionLifecycleIntent,
  type SessionOptions,
  type SessionRuntimeMetrics,
} from "./session.js";
import type { AgentProvider } from "./agent/agent-sdk-types.js";
import type {
  AgentProviderRuntimeSettingsMap,
  ProviderOverride,
} from "./agent/provider-launch-config.js";
import { ProviderSnapshotManager } from "./agent/provider-snapshot-manager.js";
import { buildProviderRegistry } from "./agent/provider-registry.js";
import { WorkspaceGitServiceImpl } from "./workspace-git-service.js";
import { PushTokenStore } from "./push/token-store.js";
import type { ClaudeTokenStore } from "./claude-token-store.js";
import { PushService } from "./push/push-service.js";
import { WebPushStore } from "./push/web-push-store.js";
import { WebPushService } from "./push/web-push-service.js";
import type { ScriptHealthState } from "./script-health-monitor.js";
import type { ScriptRouteStore } from "./script-proxy.js";
import type { WorkspaceScriptRuntimeStore } from "./workspace-script-runtime-store.js";
import type { SpeechReadinessSnapshot, SpeechService } from "./speech/speech-runtime.js";
import type { VoiceCallerContext, VoiceSpeakHandler } from "./voice-types.js";
import {
  computeShouldNotifyClient,
  computeShouldSendPush,
  type ClientAttentionState,
} from "./agent-attention-policy.js";
import {
  buildAgentAttentionNotificationPayload,
  findLatestPermissionRequest,
} from "../shared/agent-attention-notification.js";
import { reasonToEventId } from "../shared/notification-events.js";
import { createGitHubService, type GitHubService } from "../services/github-service.js";
import { createGitLabService, type GitLabService } from "../services/gitlab-service.js";
import { verifyWsJwt } from "./ws-jwt.js";
import { HostExecManager } from "./cross-host/host-exec-manager.js";
import { HostTransferManager } from "./cross-host/host-transfer-manager.js";
import { PreviewService } from "./preview/preview-service.js";
import { TcpForwardManager } from "./cross-host/tcp-forward-manager.js";

export type ExternalSocketMetadata = {
  transport: "relay";
  externalSessionKey?: string;
  /**
   * X25519 public key (base64) of the peer that completed the e2ee
   * handshake on this connection. Captured from the daemon-side hello.
   * Used downstream to resolve the connecting device → owning user via
   * the auth-server allow-list (multi-tenant session isolation). Null
   * when unknown — e.g. legacy paths before the metadata was plumbed.
   */
  peerPublicKeyB64?: string | null;
};

type PendingConnection = {
  connectionLogger: pino.Logger;
  helloTimeout: ReturnType<typeof setTimeout> | null;
  /**
   * True only for direct (non-relay) sockets whose remote address is a
   * loopback address. Gates the local CLI-token auth path — relay-attached
   * sockets are never loopback regardless of reported address.
   */
  isLoopback: boolean;
  /**
   * Multi-tenant: device pubkey captured from the e2ee handshake before
   * hello (relay path only). Carried through to `handleHello` so the
   * Session can resolve the connecting user via the auth-server allow-list.
   */
  peerPublicKeyB64: string | null;
};

type WebSocketServerConfig = {
  allowedOrigins: Set<string>;
  hostnames?: HostnamesConfig;
};

type WebSocketRuntimeMetrics = SessionRuntimeMetrics & CheckoutDiffMetrics;

function createFallbackWorkspaceGitService(): WorkspaceGitServiceImpl {
  return {
    subscribe: async ({ cwd }: { cwd: string }) => ({
      initial: {
        cwd,
        git: {
          isGit: false,
          repoRoot: null,
          mainRepoRoot: null,
          currentBranch: null,
          remoteUrl: null,
          isAppostleOwnedWorktree: false,
          isDirty: null,
          aheadBehind: null,
          aheadOfOrigin: null,
          behindOfOrigin: null,
          branchDiffStat: null,
          uncommittedDiffStat: null,
          baseRef: null,
        },
        github: {
          featuresEnabled: false,
          pullRequest: null,
          error: null,
          refreshedAt: null,
        },
      },
      unsubscribe: () => {},
    }),
    peekSnapshot: () => null,
    getSnapshot: async (cwd: string) => ({
      cwd,
      git: {
        isGit: false,
        repoRoot: null,
        mainRepoRoot: null,
        currentBranch: null,
        remoteUrl: null,
        isAppostleOwnedWorktree: false,
        isDirty: null,
        aheadBehind: null,
        aheadOfOrigin: null,
        behindOfOrigin: null,
        branchDiffStat: null,
        uncommittedDiffStat: null,
        baseRef: null,
      },
      github: {
        featuresEnabled: false,
        pullRequest: null,
        error: null,
        refreshedAt: null,
      },
    }),
    refresh: async () => {},
    requestWorkingTreeWatch: async (cwd: string) => ({
      repoRoot: cwd,
      unsubscribe: () => {},
    }),
    scheduleRefreshForCwd: () => {},
    dispose: () => {},
  } as unknown as WorkspaceGitServiceImpl;
}

function createNoopProjectRegistry(): ProjectRegistry {
  return {
    initialize: async () => {},
    existsOnDisk: async () => true,
    list: async () => [],
    get: async () => null,
    upsert: async () => {},
    archive: async () => {},
    remove: async () => {},
  };
}

function createNoopWorkspaceRegistry(): WorkspaceRegistry {
  return {
    initialize: async () => {},
    existsOnDisk: async () => true,
    list: async () => [],
    get: async () => null,
    upsert: async () => {},
    archive: async () => {},
    remove: async () => {},
  };
}

function toServerCapabilityState(params: {
  state: SpeechReadinessSnapshot["dictation"];
  reason: string;
}): ServerCapabilityState {
  const { state, reason } = params;
  return {
    enabled: state.enabled,
    reason,
  };
}

function resolveCapabilityReason(params: {
  state: SpeechReadinessSnapshot["dictation"];
  readiness: SpeechReadinessSnapshot;
}): string {
  const { state, readiness } = params;
  if (state.available) {
    return "";
  }

  if (readiness.voiceFeature.reasonCode === "model_download_in_progress") {
    const baseMessage = readiness.voiceFeature.message.trim();
    if (baseMessage.includes("Try again in a few minutes")) {
      return baseMessage;
    }
    return `${baseMessage} Try again in a few minutes.`;
  }

  return state.message;
}

function buildServerCapabilities(params: {
  readiness: SpeechReadinessSnapshot | null;
}): ServerCapabilities | undefined {
  const readiness = params.readiness;
  if (!readiness) {
    return undefined;
  }
  return {
    voice: {
      dictation: toServerCapabilityState({
        state: readiness.dictation,
        reason: resolveCapabilityReason({
          state: readiness.dictation,
          readiness,
        }),
      }),
      voice: toServerCapabilityState({
        state: readiness.realtimeVoice,
        reason: resolveCapabilityReason({
          state: readiness.realtimeVoice,
          readiness,
        }),
      }),
    },
  };
}

function areServerCapabilitiesEqual(
  current: ServerCapabilities | undefined,
  next: ServerCapabilities | undefined,
): boolean {
  return JSON.stringify(current ?? null) === JSON.stringify(next ?? null);
}

function bufferFromWsData(data: Buffer | ArrayBuffer | Buffer[] | string): Buffer {
  if (typeof data === "string") return Buffer.from(data, "utf8");
  if (Array.isArray(data)) {
    return Buffer.concat(
      data.map((item) => (Buffer.isBuffer(item) ? item : Buffer.from(item as ArrayBuffer))),
    );
  }
  if (Buffer.isBuffer(data)) return data;
  return Buffer.from(data as ArrayBuffer);
}

type WebSocketLike = {
  readyState: number;
  bufferedAmount?: number;
  send: (data: string | Uint8Array | ArrayBuffer) => void;
  close: (code?: number, reason?: string) => void;
  on: (event: "message" | "close" | "error", listener: (...args: any[]) => void) => void;
  once: (event: "close" | "error", listener: (...args: any[]) => void) => void;
};

type SessionConnection = {
  session: Session;
  clientId: string;
  appVersion: string | null;
  connectionLogger: pino.Logger;
  sockets: Set<WebSocketLike>;
  externalDisconnectCleanupTimeout: ReturnType<typeof setTimeout> | null;
  /** Auth-server userId of the connecting user (from hello message). */
  userId: string | null;
  /** Display name of the connecting user (from hello message). */
  username: string | null;
  /**
   * When set, this connection is a scoped client (Lenses piece 3) — every
   * inbound message is filtered by the client-scope dispatch guard and every
   * outbound emit is filtered by the ownership check. Full-trust staff
   * connections leave this undefined.
   */
  clientScope?: ClientScope;
};

type WebSocketRuntimeCounters = {
  connectedAwaitingHello: number;
  helloResumed: number;
  helloNew: number;
  pendingDisconnected: number;
  sessionDisconnectedWaitingReconnect: number;
  sessionSocketDisconnectedAttached: number;
  sessionCleanup: number;
  validationFailed: number;
  binaryBeforeHelloRejected: number;
  pendingMessageRejectedBeforeHello: number;
  missingConnectionForMessage: number;
  unexpectedHelloOnActiveConnection: number;
  relayExternalSocketAttached: number;
  originRejected: number;
  hostRejected: number;
  helloTcpForward: number;
  tcpForwardDisconnected: number;
  helloHostExec: number;
  hostExecDisconnected: number;
  helloHostTransfer: number;
  hostTransferDisconnected: number;
};

const SLOW_REQUEST_THRESHOLD_MS = 500;
const EXTERNAL_SESSION_DISCONNECT_GRACE_MS = 90_000;
const HELLO_TIMEOUT_MS = 15_000;
const WS_CLOSE_HELLO_TIMEOUT = 4001;
const WS_CLOSE_INVALID_HELLO = 4002;
const WS_CLOSE_INCOMPATIBLE_PROTOCOL = 4003;
const WS_PROTOCOL_VERSION = 1;
const WS_RUNTIME_METRICS_FLUSH_MS = 30_000;

export class MissingDaemonVersionError extends Error {
  constructor() {
    super("VoiceAssistantWebSocketServer requires a non-empty daemonVersion.");
    this.name = "MissingDaemonVersionError";
  }
}

/**
 * WebSocket server that only accepts sockets + parses/forwards messages to the session layer.
 */
export class VoiceAssistantWebSocketServer {
  private readonly logger: pino.Logger;
  private readonly wss: WebSocketServer;
  private readonly pendingConnections: Map<WebSocketLike, PendingConnection> = new Map();
  private readonly sessions: Map<WebSocketLike, SessionConnection> = new Map();
  private readonly externalSessionsByKey: Map<string, SessionConnection> = new Map();
  // Cross-host SSH (tcp-forward mode). A separate map so these connections are
  // invisible to agent dispatch / session bookkeeping — they're raw byte pumps,
  // not Sessions. See `cross-host/tcp-forward-manager.ts`.
  private readonly tcpForwardManager: TcpForwardManager = new TcpForwardManager();
  // Cross-host command exec (host-exec mode). Like tcp-forward, these
  // connections never allocate a Session — they spawn a shell, stream
  // stdout/stderr frames back, and close. See `cross-host/host-exec-manager.ts`.
  private readonly hostExecManager: HostExecManager = new HostExecManager();

  // Cross-host file transfer (host-transfer mode). One file per connection,
  // chunked + ack-windowed + sha256-verified. See `cross-host/host-transfer-manager.ts`.
  private readonly hostTransferManager: HostTransferManager = new HostTransferManager();
  // Element annotator: one host-side Playwright browser shared across sessions.
  private readonly previewService: PreviewService = new PreviewService();
  private readonly serverId: string;
  private readonly daemonVersion: string;
  private readonly agentManager: AgentManager;
  private readonly agentStorage: AgentStorage;
  private readonly projectRegistry: ProjectRegistry;
  private readonly workspaceRegistry: WorkspaceRegistry;
  private readonly workspaceStacksService: WorkspaceStacksService;
  private readonly todoStore: TodoStore;
  private readonly chatService: FileBackedChatService;
  private readonly loopService: LoopService;
  private readonly questService: QuestService;
  private readonly scheduleService: ScheduleService;
  private readonly checkoutDiffManager: CheckoutDiffManager;
  private readonly github: GitHubService;
  private readonly gitlab: GitLabService;
  private readonly workspaceGitService: WorkspaceGitServiceImpl;
  private readonly downloadTokenStore: DownloadTokenStore;
  private readonly appostleHome: string;
  private readonly daemonConfigStore: DaemonConfigStore;
  private readonly pushTokenStore: PushTokenStore;
  private readonly claudeTokenStore: ClaudeTokenStore;
  private readonly pushService: PushService;
  private readonly webPushStore: WebPushStore;
  private readonly webPushService: WebPushService;
  private readonly mcpBaseUrl: string | null;
  private readonly speech: SpeechService | null;
  private readonly terminalManager: TerminalManager | null;
  private readonly scriptRouteStore: ScriptRouteStore | null;
  private readonly scriptRuntimeStore: WorkspaceScriptRuntimeStore | null;
  private readonly getDaemonTcpPort: (() => number | null) | null;
  private readonly getDaemonTcpHost: (() => string | null) | null;
  private readonly resolveScriptHealth: ((hostname: string) => ScriptHealthState | null) | null;
  /**
   * Auth-server Ed25519 public key (hex), pinned at link time. Used by
   * `createSessionConnection` to verify the WS-hello JWT locally — no
   * allow-list round-trip needed. Set by bootstrap via `setServerPubkeyHex`.
   */
  private serverPubkeyHex: string | null = null;
  /**
   * Local CLI trust: per-boot random token written to $APPOSTLE_HOME/cli-token
   * (0600). A hello presenting it over a loopback (non-relay) socket
   * authenticates as the daemon owner without an auth-server JWT — the CLI
   * has no account context to mint one. Set by bootstrap via `setCliToken`.
   */
  private cliToken: string | null = null;
  private cliTokenOwnerUserId: string | null = null;
  /**
   * Bootstrap supplies this so a successful `link_account_request` can bring
   * the auth-server client up in-process — see `handleLinkAccountRequest`
   * in session.ts and gitlab issue #24.
   */
  private onAccountLinkedCallback: SessionOptions["onAccountLinked"] | null = null;
  /**
   * When true the daemon booted in `awaiting_link` mode — there is no
   * auth-server client and therefore no pubkey resolver. New sessions are
   * still created (so the relay path can carry a `link_account_request`),
   * but every non-link inbound message is rejected by the session itself.
   * See bootstrap.ts and Session.handleMessage.
   */
  private awaitingLinkMode = false;
  private readonly dictation: {
    finalTimeoutMs?: number;
  } | null;
  private readonly voiceSpeakHandlers = new Map<string, VoiceSpeakHandler>();
  private readonly voiceCallerContexts = new Map<string, VoiceCallerContext>();
  private readonly agentProviderRuntimeSettings: AgentProviderRuntimeSettingsMap | undefined;
  private readonly providerOverrides: Record<string, ProviderOverride> | undefined;
  private readonly providerSnapshotManager: ProviderSnapshotManager;
  private readonly onLifecycleIntent: ((intent: SessionLifecycleIntent) => void) | null;
  private readonly onBranchChanged:
    | ((workspaceId: string, oldBranch: string | null, newBranch: string | null) => void)
    | null;
  private readonly onWorkspaceArchived: ((workspace: PersistedWorkspaceRecord) => void) | null;
  private readonly onWorkspaceUnarchived: ((workspace: PersistedWorkspaceRecord) => void) | null;
  private serverCapabilities: ServerCapabilities | undefined;
  private runtimeWindowStartedAt = Date.now();
  private readonly runtimeCounters: WebSocketRuntimeCounters = {
    connectedAwaitingHello: 0,
    helloResumed: 0,
    helloNew: 0,
    pendingDisconnected: 0,
    sessionDisconnectedWaitingReconnect: 0,
    sessionSocketDisconnectedAttached: 0,
    sessionCleanup: 0,
    validationFailed: 0,
    binaryBeforeHelloRejected: 0,
    pendingMessageRejectedBeforeHello: 0,
    missingConnectionForMessage: 0,
    unexpectedHelloOnActiveConnection: 0,
    relayExternalSocketAttached: 0,
    originRejected: 0,
    hostRejected: 0,
    helloTcpForward: 0,
    tcpForwardDisconnected: 0,
    helloHostExec: 0,
    hostExecDisconnected: 0,
    helloHostTransfer: 0,
    hostTransferDisconnected: 0,
  };
  private readonly inboundMessageCounts = new Map<string, number>();
  private readonly inboundSessionRequestCounts = new Map<string, number>();
  private readonly outboundMessageCounts = new Map<string, number>();
  private readonly outboundSessionMessageCounts = new Map<string, number>();
  private readonly outboundAgentStreamCounts = new Map<string, number>();
  private readonly outboundAgentStreamByAgentCounts = new Map<string, number>();
  private readonly outboundBinaryFrameCounts = new Map<string, number>();
  private readonly bufferedAmountSamples: number[] = [];
  private readonly requestLatencies = new Map<string, number[]>();
  private runtimeMetricsInterval: ReturnType<typeof setInterval> | null = null;
  private unsubscribeSpeechReadiness: (() => void) | null = null;
  private unsubscribeDaemonConfigChange: (() => void) | null = null;

  constructor(
    server: HTTPServer,
    logger: pino.Logger,
    serverId: string,
    agentManager: AgentManager,
    agentStorage: AgentStorage,
    downloadTokenStore: DownloadTokenStore,
    appostleHome: string,
    daemonConfigStore: DaemonConfigStore,
    mcpBaseUrl: string | null,
    wsConfig: WebSocketServerConfig,
    speech?: SpeechService | null,
    terminalManager?: TerminalManager | null,
    dictation?: {
      finalTimeoutMs?: number;
    },
    agentProviderRuntimeSettings?: AgentProviderRuntimeSettingsMap,
    providerOverrides?: Record<string, ProviderOverride>,
    daemonVersion?: string,
    onLifecycleIntent?: (intent: SessionLifecycleIntent) => void,
    projectRegistry?: ProjectRegistry,
    workspaceRegistry?: WorkspaceRegistry,
    chatService?: FileBackedChatService,
    loopService?: LoopService,
    questService?: QuestService,
    scheduleService?: ScheduleService,
    checkoutDiffManager?: CheckoutDiffManager,
    scriptRouteStore?: ScriptRouteStore | null,
    scriptRuntimeStore?: WorkspaceScriptRuntimeStore | null,
    onBranchChanged?: (
      workspaceId: string,
      oldBranch: string | null,
      newBranch: string | null,
    ) => void,
    getDaemonTcpPort?: () => number | null,
    getDaemonTcpHost?: () => string | null,
    resolveScriptHealth?: (hostname: string) => ScriptHealthState | null,
    workspaceGitService?: WorkspaceGitServiceImpl,
    github?: GitHubService,
    gitlab?: GitLabService,
    workspaceStacksService?: WorkspaceStacksService,
    todoStore?: TodoStore,
    claudeTokenStore?: ClaudeTokenStore,
    onWorkspaceArchived?: (workspace: PersistedWorkspaceRecord) => void,
    onWorkspaceUnarchived?: (workspace: PersistedWorkspaceRecord) => void,
  ) {
    this.logger = logger.child({ module: "websocket-server" });
    this.serverId = serverId;
    if (typeof daemonVersion !== "string" || daemonVersion.trim().length === 0) {
      throw new MissingDaemonVersionError();
    }
    this.daemonVersion = daemonVersion.trim();
    this.agentManager = agentManager;
    this.agentStorage = agentStorage;
    this.projectRegistry = projectRegistry ?? createNoopProjectRegistry();
    this.workspaceRegistry = workspaceRegistry ?? createNoopWorkspaceRegistry();
    if (!workspaceStacksService) {
      throw new Error("VoiceAssistantWebSocketServer requires a workspace stacks service.");
    }
    this.workspaceStacksService = workspaceStacksService;
    if (!todoStore) {
      throw new Error("VoiceAssistantWebSocketServer requires a todo store.");
    }
    this.todoStore = todoStore;
    if (!claudeTokenStore) {
      throw new Error("VoiceAssistantWebSocketServer requires a claude token store.");
    }
    this.claudeTokenStore = claudeTokenStore;
    if (!chatService) {
      throw new Error("VoiceAssistantWebSocketServer requires a chat service.");
    }
    this.chatService = chatService;
    if (!loopService) {
      throw new Error("VoiceAssistantWebSocketServer requires a loop service.");
    }
    this.loopService = loopService;
    if (!questService) {
      throw new Error("VoiceAssistantWebSocketServer requires a quest service.");
    }
    this.questService = questService;
    if (!scheduleService) {
      throw new Error("VoiceAssistantWebSocketServer requires a schedule service.");
    }
    this.scheduleService = scheduleService;
    if (!checkoutDiffManager) {
      throw new Error("VoiceAssistantWebSocketServer requires a checkout diff manager.");
    }
    this.checkoutDiffManager = checkoutDiffManager;
    this.github = github ?? createGitHubService();
    this.gitlab = gitlab ?? createGitLabService();
    this.workspaceGitService = workspaceGitService ?? createFallbackWorkspaceGitService();
    this.downloadTokenStore = downloadTokenStore;
    this.appostleHome = appostleHome;
    this.daemonConfigStore = daemonConfigStore;
    this.mcpBaseUrl = mcpBaseUrl;
    this.speech = speech ?? null;
    this.terminalManager = terminalManager ?? null;
    this.dictation = dictation ?? null;
    this.agentProviderRuntimeSettings = agentProviderRuntimeSettings;
    this.providerOverrides = providerOverrides;
    const providerSnapshotLogger = this.logger.child({ module: "provider-snapshot-manager" });
    this.providerSnapshotManager = new ProviderSnapshotManager(
      buildProviderRegistry(providerSnapshotLogger, {
        runtimeSettings: this.agentProviderRuntimeSettings,
        providerOverrides: this.providerOverrides,
      }),
      providerSnapshotLogger,
    );
    this.onLifecycleIntent = onLifecycleIntent ?? null;
    this.scriptRouteStore = scriptRouteStore ?? null;
    this.scriptRuntimeStore = scriptRuntimeStore ?? null;
    this.onBranchChanged = onBranchChanged ?? null;
    this.onWorkspaceArchived = onWorkspaceArchived ?? null;
    this.onWorkspaceUnarchived = onWorkspaceUnarchived ?? null;
    this.getDaemonTcpPort = getDaemonTcpPort ?? null;
    this.getDaemonTcpHost = getDaemonTcpHost ?? null;
    this.resolveScriptHealth = resolveScriptHealth ?? null;
    this.serverCapabilities = buildServerCapabilities({
      readiness: this.speech?.getReadiness() ?? null,
    });
    this.unsubscribeSpeechReadiness =
      this.speech?.onReadinessChange((snapshot) => {
        this.publishSpeechReadiness(snapshot);
      }) ?? null;
    this.unsubscribeDaemonConfigChange = this.daemonConfigStore.onChange((config) => {
      this.broadcastDaemonConfigChanged(config);
    });
    // Re-broadcast server_info when the daemon icon changes so every paired
    // client picks up the new icon in their host switcher without needing to
    // reconnect.
    this.daemonConfigStore.onFieldChange("identity.icon", () => {
      this.broadcastCapabilitiesUpdate();
    });

    const pushLogger = this.logger.child({ module: "push" });
    this.pushTokenStore = new PushTokenStore(pushLogger, join(appostleHome, "push-tokens.json"));
    this.pushService = new PushService(pushLogger, this.pushTokenStore);
    this.webPushStore = new WebPushStore(
      pushLogger,
      join(appostleHome, "vapid.json"),
      join(appostleHome, "web-push-subs.json"),
    );
    this.webPushService = new WebPushService(pushLogger, this.webPushStore);

    this.agentManager.setAgentAttentionCallback((params) => {
      void this.broadcastAgentAttention(params).catch((err) => {
        this.logger.warn({ err, agentId: params.agentId }, "Failed to broadcast agent attention");
      });
    });

    const { allowedOrigins, hostnames } = wsConfig;
    // Use `noServer` (NOT `{ server, path }`) so this WS server can share the
    // daemon's HTTP port with other upgrade listeners (e.g. the local
    // tcp-forward listener at `/local/ws-tcp-forward`). A `{ server, path }`
    // WebSocketServer auto-attaches an upgrade handler that ABORTS every upgrade
    // whose path !== the configured one with a 400 — which corrupts the socket
    // that a second listener is concurrently upgrading (manifests downstream as
    // "Invalid WebSocket frame: RSV1 must be clear"). With `noServer` we own the
    // dispatch: handle `/ws`, and simply ignore other paths so their own
    // listeners can claim them.
    this.wss = new WebSocketServer({ noServer: true });

    this.wss.on("connection", (ws, request) => {
      void this.attachSocket(ws, request);
    });

    const abortUpgrade = (
      socket: import("node:stream").Duplex,
      code: number,
      message: string,
    ): void => {
      if (socket.writable) {
        socket.write(
          `HTTP/1.1 ${code} ${message}\r\n` +
            "Connection: close\r\n" +
            "Content-Type: text/plain\r\n" +
            `Content-Length: ${Buffer.byteLength(message)}\r\n` +
            "\r\n" +
            message,
        );
      }
      socket.destroy();
    };

    server.on("upgrade", (req, socket, head) => {
      const pathname = (req.url ?? "").split("?")[0];
      if (pathname !== "/ws") {
        // Not our endpoint — leave the socket for other upgrade listeners
        // (e.g. the local tcp-forward listener). Do NOT abort it here, or we
        // corrupt a connection another listener owns.
        return;
      }
      const requestMetadata = extractSocketRequestMetadata(req);
      const origin = requestMetadata.origin;
      const requestHost = requestMetadata.host ?? null;
      if (requestHost && !isHostnameAllowed(requestHost, hostnames)) {
        this.incrementRuntimeCounter("hostRejected");
        this.logger.warn(
          { ...requestMetadata, host: requestHost },
          "Rejected connection from disallowed host",
        );
        abortUpgrade(socket, 403, "Host not allowed");
        return;
      }
      const sameOrigin =
        !!origin &&
        !!requestHost &&
        (origin === `http://${requestHost}` || origin === `https://${requestHost}`);

      if (!origin || allowedOrigins.has("*") || allowedOrigins.has(origin) || sameOrigin) {
        this.wss.handleUpgrade(req, socket, head, (ws) => {
          this.wss.emit("connection", ws, req);
        });
      } else {
        this.incrementRuntimeCounter("originRejected");
        this.logger.warn({ ...requestMetadata, origin }, "Rejected connection from origin");
        abortUpgrade(socket, 403, "Origin not allowed");
      }
    });

    const runtimeMetricsInterval = setInterval(() => {
      this.flushRuntimeMetrics();
    }, WS_RUNTIME_METRICS_FLUSH_MS);
    this.runtimeMetricsInterval = runtimeMetricsInterval;
    (runtimeMetricsInterval as unknown as { unref?: () => void }).unref?.();

    this.logger.info("WebSocket server initialized on /ws");
  }

  public broadcast(message: WSOutboundMessage): void {
    const payload = JSON.stringify(message);
    for (const ws of this.sessions.keys()) {
      // WebSocket.OPEN = 1
      if (ws.readyState === 1) {
        ws.send(payload);
        this.recordOutboundMessage(message, ws);
      }
    }
  }

  /**
   * Pin the auth-server's Ed25519 public key (hex) so the WS hello handler
   * can verify JWTs locally. Called by bootstrap after the daemon links to
   * the auth-server; also updated on account rotation.
   */
  public setCliToken(token: string, ownerUserId: string): void {
    this.cliToken = token;
    this.cliTokenOwnerUserId = ownerUserId;
  }

  public setServerPubkeyHex(hex: string): void {
    this.serverPubkeyHex = hex;
  }

  /**
   * Lookup for the scoped-allowlist section (Lenses piece 3). Set by bootstrap
   * to `authServerClient.getScopedForPubkey` after the auth-server client is
   * running. When null, all connections with `scope: "client-grants"` are
   * rejected — the daemon can't verify the grant so it fails closed.
   */
  private scopedLookup: ((pubkey: string) => ClientScope | null) | null = null;
  public setScopedLookup(fn: ((pubkey: string) => ClientScope | null) | null): void {
    this.scopedLookup = fn;
  }

  /**
   * Wire the on-account-linked callback. Called by bootstrap once at startup.
   * The callback fires from `Session.handleLinkAccountRequest` after a
   * successful link, so the daemon can start its auth-server client without
   * needing an operator restart.
   */
  public setOnAccountLinked(fn: NonNullable<SessionOptions["onAccountLinked"]>): void {
    this.onAccountLinkedCallback = fn;
  }

  /**
   * Put the WS server into `awaiting_link` mode. Called by bootstrap when
   * the daemon was started without an `account` block in
   * `~/.appostle/config.json`. In this mode:
   *   - new sessions are accepted WITHOUT a pubkey-resolver (no auth-server
   *     client exists yet), so the relay path can deliver a
   *     `link_account_request`;
   *   - every session is constructed with `awaitingLink: true` and rejects
   *     every other inbound message with `daemon_awaiting_link_response`.
   * Bootstrap will `process.exit(0)` after a successful link so systemd
   * restarts the unit into the linked code path.
   */
  public setAwaitingLink(value: boolean): void {
    this.awaitingLinkMode = value;
  }

  /**
   * Force-close every active WebSocket session with the given code/reason.
   * Used when the daemon transitions between auth regimes (e.g. just-linked
   * to an auth-server) and any in-flight pre-transition sessions must
   * reconnect under the new rules.
   */
  public closeAllSessions(code: number, reason: string): void {
    const targets = [...this.sessions.keys(), ...this.pendingConnections.keys()];
    for (const ws of targets) {
      try {
        ws.close(code, reason);
      } catch {
        // best-effort; close races are fine
      }
    }
  }

  /**
   * Close all open WebSocket connections belonging to the given userId with
   * WS close code 1008 (Policy Violation). Returns the number of sockets
   * closed. Idempotent — returns 0 if the user has no open sessions.
   * The sentinel `__awaiting_link__` is skipped (not a real user).
   */
  /**
   * Push an outbound message to every connected WebSocket session
   * owned by `userId`. Used by the tab-sync hydration path so a share
   * grant or revoke fans out to the recipient(s) immediately instead
   * of waiting for their next polling cycle.
   *
   * Returns the number of sessions that received the message.
   * Idempotent — no-op when the user isn't connected.
   */
  public emitMessageToUser(userId: string, message: SessionOutboundMessage): number {
    if (userId === "__awaiting_link__") return 0;
    let delivered = 0;
    for (const connection of this.sessions.values()) {
      if (connection.session.getOwnerUserId() === userId) {
        try {
          connection.session.emitServerMessage(message);
          delivered++;
        } catch (err) {
          this.logger.warn(
            { err, userId, type: message.type },
            "emitMessageToUser: failed to push",
          );
        }
      }
    }
    return delivered;
  }

  /**
   * Fan a todo mutation out to every session (except the originator) that is
   * subscribed to the given workspaceId. Fire-and-forget; errors are caught
   * per-session and logged. Called from the `onTodoMutation` callback wired at
   * session construction, and from bootstrap's agent-initiated todo paths.
   */
  public broadcastTodoListState(workspaceId: string, excludeSession?: Session): void {
    for (const connection of this.sessions.values()) {
      const target = connection.session;
      if (target === excludeSession) continue;
      void target.pushTodoListStateIfSubscribed(workspaceId).catch((err) => {
        this.logger.warn({ err, workspaceId }, "broadcastTodoListState: failed to push to session");
      });
    }
  }

  public closeSessionsForUser(userId: string, reason: string): number {
    if (userId === "__awaiting_link__") return 0;
    let closed = 0;
    for (const [ws, connection] of this.sessions) {
      if (connection.session.getOwnerUserId() === userId) {
        try {
          ws.close(1008, reason);
          closed++;
        } catch {
          // best-effort; close races are fine
        }
      }
    }
    this.logger.info({ userId, reason, closed }, "closeSessionsForUser");
    return closed;
  }

  public listActiveSessions(): Session[] {
    return Array.from(
      new Set(
        [...this.sessions.values(), ...this.externalSessionsByKey.values()].map(
          (connection) => connection.session,
        ),
      ),
    );
  }

  public publishSpeechReadiness(readiness: SpeechReadinessSnapshot | null): void {
    this.updateServerCapabilities(buildServerCapabilities({ readiness }));
  }

  public updateServerCapabilities(capabilities: ServerCapabilities | null | undefined): void {
    const next = capabilities ?? undefined;
    if (areServerCapabilitiesEqual(this.serverCapabilities, next)) {
      return;
    }
    this.serverCapabilities = next;
    this.broadcastCapabilitiesUpdate();
  }

  public async attachExternalSocket(
    ws: WebSocketLike,
    metadata?: ExternalSocketMetadata,
  ): Promise<void> {
    if (metadata?.transport === "relay") {
      this.incrementRuntimeCounter("relayExternalSocketAttached");
    }
    await this.attachSocket(ws, undefined, metadata);
  }

  public async close(): Promise<void> {
    this.unsubscribeSpeechReadiness?.();
    this.unsubscribeSpeechReadiness = null;
    this.unsubscribeDaemonConfigChange?.();
    this.unsubscribeDaemonConfigChange = null;
    if (this.runtimeMetricsInterval) {
      clearInterval(this.runtimeMetricsInterval);
      this.runtimeMetricsInterval = null;
    }
    this.flushRuntimeMetrics({ final: true });

    const uniqueConnections = new Set<SessionConnection>([
      ...this.sessions.values(),
      ...this.externalSessionsByKey.values(),
    ]);

    const pendingSockets = new Set<WebSocketLike>(this.pendingConnections.keys());
    for (const pending of this.pendingConnections.values()) {
      if (pending.helloTimeout) {
        clearTimeout(pending.helloTimeout);
        pending.helloTimeout = null;
      }
    }

    const cleanupPromises: Promise<void>[] = [];
    for (const connection of uniqueConnections) {
      if (connection.externalDisconnectCleanupTimeout) {
        clearTimeout(connection.externalDisconnectCleanupTimeout);
        connection.externalDisconnectCleanupTimeout = null;
      }

      cleanupPromises.push(connection.session.cleanup());
      for (const ws of connection.sockets) {
        cleanupPromises.push(
          new Promise<void>((resolve) => {
            // WebSocket.CLOSED = 3
            if (ws.readyState === 3) {
              resolve();
              return;
            }
            ws.once("close", () => resolve());
            ws.close();
          }),
        );
      }
    }

    for (const ws of pendingSockets) {
      cleanupPromises.push(
        new Promise<void>((resolve) => {
          if (ws.readyState === 3) {
            resolve();
            return;
          }
          ws.once("close", () => resolve());
          ws.close();
        }),
      );
    }

    await Promise.all(cleanupPromises);
    this.providerSnapshotManager.destroy();
    this.checkoutDiffManager.dispose();
    this.workspaceGitService.dispose();
    this.pendingConnections.clear();
    this.sessions.clear();
    this.externalSessionsByKey.clear();
    this.wss.close();
  }

  private sendToClient(ws: WebSocketLike, message: WSOutboundMessage): void {
    // WebSocket.OPEN = 1
    if (ws.readyState === 1) {
      ws.send(JSON.stringify(message));
      this.recordOutboundMessage(message, ws);
    }
  }

  private sendBinaryToClient(ws: WebSocketLike, frame: Uint8Array): void {
    if (ws.readyState !== 1) {
      return;
    }
    ws.send(frame);
    this.recordOutboundBinaryFrame(ws);
  }

  private sendToConnection(connection: SessionConnection, message: WSOutboundMessage): void {
    for (const ws of connection.sockets) {
      this.sendToClient(ws, message);
    }
  }

  private sendBinaryToConnection(connection: SessionConnection, frame: Uint8Array): void {
    for (const ws of connection.sockets) {
      this.sendBinaryToClient(ws, frame);
    }
  }

  private async attachSocket(
    ws: WebSocketLike,
    request?: unknown,
    metadata?: ExternalSocketMetadata,
  ): Promise<void> {
    const requestMetadata = extractSocketRequestMetadata(request);
    const connectionLoggerFields: Record<string, string> = {
      transport: metadata?.transport === "relay" ? "relay" : "direct",
    };
    if (requestMetadata.host) {
      connectionLoggerFields.host = requestMetadata.host;
    }
    if (requestMetadata.origin) {
      connectionLoggerFields.origin = requestMetadata.origin;
    }
    if (requestMetadata.userAgent) {
      connectionLoggerFields.userAgent = requestMetadata.userAgent;
    }
    if (requestMetadata.remoteAddress) {
      connectionLoggerFields.remoteAddress = requestMetadata.remoteAddress;
    }
    const connectionLogger = this.logger.child(connectionLoggerFields);

    const pending: PendingConnection = {
      connectionLogger,
      helloTimeout: null,
      peerPublicKeyB64: metadata?.peerPublicKeyB64 ?? null,
      isLoopback:
        metadata?.transport !== "relay" && isLoopbackAddress(requestMetadata.remoteAddress ?? null),
    };
    const timeout = setTimeout(() => {
      if (this.pendingConnections.get(ws) !== pending) {
        return;
      }
      pending.helloTimeout = null;
      this.pendingConnections.delete(ws);
      pending.connectionLogger.warn(
        { timeoutMs: HELLO_TIMEOUT_MS },
        "Closing connection due to missing hello",
      );
      try {
        ws.close(WS_CLOSE_HELLO_TIMEOUT, "Hello timeout");
      } catch {
        // ignore close errors
      }
    }, HELLO_TIMEOUT_MS);
    pending.helloTimeout = timeout;
    (timeout as unknown as { unref?: () => void }).unref?.();

    this.pendingConnections.set(ws, pending);
    this.incrementRuntimeCounter("connectedAwaitingHello");
    this.bindSocketHandlers(ws);

    pending.connectionLogger.trace(
      {
        totalPendingConnections: this.pendingConnections.size,
      },
      "Client connected; awaiting hello",
    );
  }

  private createSessionConnection(params: {
    ws: WebSocketLike;
    clientId: string;
    appVersion: string | null;
    connectionLogger: pino.Logger;
    /** Device pubkey from e2ee handshake; null for local TCP or legacy paths. */
    peerPublicKeyB64: string | null;
    /**
     * Self-declared userId / username from the hello message. Used for UI
     * display + ownership tracking. Authoritative ownership is now derived
     * from the verified `wsToken` JWT (`ownerUserId` below) — never trust
     * these fields for access control.
     */
    userId?: string | null;
    username?: string | null;
    /**
     * Short-lived EdDSA JWT from POST /auth/ws-token. Required for all
     * authenticated connections. The daemon verifies the signature with the
     * pinned serverPubkeyHex and extracts ownerUserId from the `sub` claim.
     * Missing or invalid → 1008 auth_required.
     */
    wsToken: string;
    /** True for direct loopback sockets — gates the local CLI-token path. */
    isLoopback: boolean;
  }): SessionConnection {
    const { ws, clientId, appVersion, connectionLogger, peerPublicKeyB64 } = params;
    let connection: SessionConnection | null = null;

    // awaiting_link bypass — the daemon booted without an `account` block,
    // so there is no auth-server client and therefore no resolver. We still
    // accept the session (so the relay path can deliver a
    // `link_account_request`), but pin a sentinel ownerUserId that can
    // never match a real user. The Session itself rejects every non-link
    // message with `daemon_awaiting_link_response`, so the sentinel never
    // gates anything besides agent visibility (and there are no agents
    // loaded in this mode either).
    let ownerUserId: string;
    if (this.awaitingLinkMode) {
      ownerUserId = "__awaiting_link__";
      connectionLogger.info(
        { peerPublicKeyB64: peerPublicKeyB64 ? "present" : null },
        "session_accepted_awaiting_link",
      );
    } else if (
      params.isLoopback &&
      this.cliToken !== null &&
      this.cliTokenOwnerUserId !== null &&
      timingSafeTokenEqual(params.wsToken, this.cliToken)
    ) {
      // Local CLI trust: the token only exists in $APPOSTLE_HOME/cli-token
      // (0600), so presenting it over loopback proves same-user local access.
      ownerUserId = this.cliTokenOwnerUserId;
      connectionLogger.info({ ownerUserId }, "session_owner_resolved_cli_token");
    } else {
      // Verify the short-lived JWT from the hello message. The JWT was minted
      // by the auth-server (POST /auth/ws-token) and signed with the Ed25519
      // key the daemon pinned at link time. No allow-list round-trip needed —
      // revocation is enforced at mint time on the auth-server side.
      if (!this.serverPubkeyHex) {
        connectionLogger.warn(
          { peerPublicKeyB64: peerPublicKeyB64 ?? null },
          "session_rejected_no_server_pubkey",
        );
        try {
          ws.close(1008, "auth_required: daemon not linked");
        } catch {
          // Best-effort close; if it throws the WS is already gone.
        }
        throw new Error("auth_required: daemon not linked");
      }
      const jwtResult = verifyWsJwt(
        params.wsToken,
        this.serverPubkeyHex,
        peerPublicKeyB64,
        this.serverId ?? null,
      );
      if (!jwtResult.ok) {
        connectionLogger.warn(
          {
            peerPublicKeyB64: peerPublicKeyB64 ?? null,
            helloUserId: params.userId,
            jwtReason: jwtResult.reason,
          },
          "session_rejected_invalid_jwt",
        );
        try {
          ws.close(1008, `auth_required: ${jwtResult.reason}`);
        } catch {
          // Best-effort close; if it throws the WS is already gone.
        }
        throw new Error(`auth_required: ${jwtResult.reason}`);
      }
      ownerUserId = jwtResult.userId;
      // Scoped connection (Lenses piece 3). The JWT carries `scope: "client-grants"`
      // when the auth-server minted it under the client-grants fallback path.
      // Look the client up in the scoped section of the current allowlist; if
      // the grant was revoked (or the auth-server hasn't sent a scoped section
      // yet), reject the connection.
      if (jwtResult.scope === "client-grants") {
        if (!peerPublicKeyB64) {
          connectionLogger.warn(
            { helloUserId: params.userId },
            "session_rejected_client_scope_no_peer",
          );
          try {
            ws.close(1008, "client_scope_missing");
          } catch {
            // ignore
          }
          throw new Error("client_scope_missing: no peer pubkey");
        }
        const lookup = this.scopedLookup;
        const scope = lookup ? lookup(peerPublicKeyB64) : null;
        if (!scope || scope.userId !== ownerUserId) {
          connectionLogger.warn(
            {
              peerPublicKeyB64: "present",
              ownerUserId,
              hasLookup: !!lookup,
              found: !!scope,
            },
            "session_rejected_client_scope_missing",
          );
          try {
            ws.close(1008, "client_scope_missing");
          } catch {
            // ignore
          }
          throw new Error("client_scope_missing");
        }
        connectionLogger.info(
          { ownerUserId, grantCount: scope.grants.length },
          "session_owner_resolved_jwt_scoped",
        );
      } else {
        connectionLogger.info(
          {
            peerPublicKeyB64: peerPublicKeyB64 ? "present" : null,
            ownerUserId,
            helloUserId: params.userId,
          },
          "session_owner_resolved_jwt",
        );
      }
    }
    // Compute the scope once — used both for Session construction and for
    // the SessionConnection tag. Recomputes cleanly whether we came in via
    // the JWT path or via awaiting_link / cli-token (both non-scoped).
    let clientScopeForSession: ClientScope | undefined;
    if (!this.awaitingLinkMode && this.scopedLookup && peerPublicKeyB64) {
      const s = this.scopedLookup(peerPublicKeyB64);
      if (s && s.userId === ownerUserId) {
        clientScopeForSession = s;
      }
    }

    const session = new Session({
      clientId,
      appVersion,
      peerPublicKeyB64,
      // Authoritative — derived from the e2ee pubkey via the signed
      // auth-server allow-list. Used for `canAccessAgent` checks.
      ownerUserId,
      // Self-declared by the client in the hello — display/metadata only.
      userId: params.userId,
      username: params.username,
      onMessage: (msg) => {
        if (!connection) {
          return;
        }
        this.sendToConnection(connection, wrapSessionMessage(msg));
      },
      onBinaryMessage: (frame) => {
        if (!connection) {
          return;
        }
        this.sendBinaryToConnection(connection, frame);
      },
      onLifecycleIntent: (intent) => {
        this.onLifecycleIntent?.(intent);
      },
      logger: connectionLogger.child({ module: "session" }),
      downloadTokenStore: this.downloadTokenStore,
      pushTokenStore: this.pushTokenStore,
      claudeTokenStore: this.claudeTokenStore,
      webPushStore: this.webPushStore,
      webPushService: this.webPushService,
      pushService: this.pushService,
      appostleHome: this.appostleHome,
      agentManager: this.agentManager,
      agentStorage: this.agentStorage,
      projectRegistry: this.projectRegistry,
      workspaceRegistry: this.workspaceRegistry,
      workspaceStacksService: this.workspaceStacksService,
      todoStore: this.todoStore,
      chatService: this.chatService,
      previewService: this.previewService,
      loopService: this.loopService,
      questService: this.questService,
      scheduleService: this.scheduleService,
      checkoutDiffManager: this.checkoutDiffManager,
      github: this.github,
      gitlab: this.gitlab,
      workspaceGitService: this.workspaceGitService,
      daemonConfigStore: this.daemonConfigStore,
      mcpBaseUrl: this.mcpBaseUrl,
      stt: () => this.speech?.resolveStt() ?? null,
      tts: () => this.speech?.resolveTts() ?? null,
      terminalManager: this.terminalManager,
      providerSnapshotManager: this.providerSnapshotManager,
      scriptRouteStore: this.scriptRouteStore ?? undefined,
      scriptRuntimeStore: this.scriptRuntimeStore ?? undefined,
      onBranchChanged: this.onBranchChanged ?? undefined,
      onWorkspaceArchived: this.onWorkspaceArchived ?? undefined,
      onWorkspaceUnarchived: this.onWorkspaceUnarchived ?? undefined,
      getDaemonTcpPort: this.getDaemonTcpPort ?? undefined,
      getDaemonTcpHost: this.getDaemonTcpHost ?? undefined,
      resolveScriptHealth: this.resolveScriptHealth ?? undefined,
      voice: {
        turnDetection: () => this.speech?.resolveTurnDetection() ?? null,
      },
      voiceBridge: {
        registerVoiceSpeakHandler: (agentId, handler) => {
          this.voiceSpeakHandlers.set(agentId, handler);
        },
        unregisterVoiceSpeakHandler: (agentId) => {
          this.voiceSpeakHandlers.delete(agentId);
        },
        registerVoiceCallerContext: (agentId, context) => {
          this.voiceCallerContexts.set(agentId, context);
        },
        unregisterVoiceCallerContext: (agentId) => {
          this.voiceCallerContexts.delete(agentId);
        },
      },
      dictation:
        this.dictation || this.speech
          ? {
              finalTimeoutMs: this.dictation?.finalTimeoutMs,
              stt: () => this.speech?.resolveDictationStt() ?? null,
              getSpeechReadiness: () => this.speech!.getReadiness(),
            }
          : undefined,
      agentProviderRuntimeSettings: this.agentProviderRuntimeSettings,
      providerOverrides: this.providerOverrides,
      onAccountLinked: this.onAccountLinkedCallback ?? undefined,
      awaitingLink: this.awaitingLinkMode,
      onTodoMutation: (workspaceId) => {
        this.broadcastTodoListState(workspaceId, session);
      },
    });

    connection = {
      session,
      clientId,
      appVersion,
      connectionLogger,
      sockets: new Set([ws]),
      externalDisconnectCleanupTimeout: null,
      userId: params.userId ?? null,
      username: params.username ?? null,
      ...(clientScopeForSession ? { clientScope: clientScopeForSession } : {}),
    };
    return connection;
  }

  private clearPendingConnection(ws: WebSocketLike): PendingConnection | null {
    const pending = this.pendingConnections.get(ws);
    if (!pending) {
      return null;
    }
    if (pending.helloTimeout) {
      clearTimeout(pending.helloTimeout);
      pending.helloTimeout = null;
    }
    this.pendingConnections.delete(ws);
    return pending;
  }

  private handleHello(params: {
    ws: WebSocketLike;
    message: WSHelloMessage;
    pending: PendingConnection;
  }): void {
    const { ws, message, pending } = params;

    if (message.protocolVersion !== WS_PROTOCOL_VERSION) {
      this.clearPendingConnection(ws);
      pending.connectionLogger.warn(
        {
          receivedProtocolVersion: message.protocolVersion,
          expectedProtocolVersion: WS_PROTOCOL_VERSION,
        },
        "Rejected hello due to protocol version mismatch",
      );
      try {
        ws.close(WS_CLOSE_INCOMPATIBLE_PROTOCOL, "Incompatible protocol version");
      } catch {
        // ignore close errors
      }
      return;
    }

    pending.connectionLogger.info(
      { userIdFromMessage: message.userId, hasUser: !!message.userId },
      "handleHello_userId",
    );
    const clientId = message.clientId.trim();
    if (clientId.length === 0) {
      this.clearPendingConnection(ws);
      pending.connectionLogger.warn("Rejected hello with empty clientId");
      try {
        ws.close(WS_CLOSE_INVALID_HELLO, "Invalid hello");
      } catch {
        // ignore close errors
      }
      return;
    }

    this.clearPendingConnection(ws);

    // Cross-host SSH: hello with `mode: "tcp-forward"` opens a raw byte pump
    // to `127.0.0.1:22` instead of a Session. Verify the wsToken locally
    // (defence-in-depth), gate the target to loopback:22, and hand off to the
    // manager. The manager owns the entire lifetime of this connection — it
    // will close the WS on TCP error/close and on disconnect.
    if (message.mode === "tcp-forward") {
      this.incrementRuntimeCounter("helloTcpForward");
      this.tcpForwardManager.accept({
        ws,
        logger: pending.connectionLogger.child({ clientId }),
        peerPublicKeyB64: pending.peerPublicKeyB64,
        wsToken: message.wsToken,
        target: message.target,
        serverPubkeyHex: this.serverPubkeyHex,
        serverId: this.serverId,
      });
      return;
    }

    // Cross-host command exec: hello with `mode: "host-exec"` spawns the owner's
    // login shell running `exec.command` instead of a Session. The same local
    // wsToken re-verification applies. The manager owns the connection lifetime:
    // it streams stdout/stderr frames back, sends a final exit frame, and closes.
    if (message.mode === "host-exec") {
      this.incrementRuntimeCounter("helloHostExec");
      this.hostExecManager.accept({
        ws,
        logger: pending.connectionLogger.child({ clientId }),
        peerPublicKeyB64: pending.peerPublicKeyB64,
        wsToken: message.wsToken,
        exec: message.exec,
        serverPubkeyHex: this.serverPubkeyHex,
        serverId: this.serverId,
      });
      return;
    }

    // Cross-host file transfer: hello with `mode: "host-transfer"` moves one
    // file between the dialer and this host (`get` streams out, `put` streams
    // in). Same wsToken re-verification; no Session. The manager owns the
    // connection lifetime and the inbound frame leg (acks / chunks).
    if (message.mode === "host-transfer") {
      this.incrementRuntimeCounter("helloHostTransfer");
      this.hostTransferManager.accept({
        ws,
        logger: pending.connectionLogger.child({ clientId }),
        peerPublicKeyB64: pending.peerPublicKeyB64,
        wsToken: message.wsToken,
        transfer: message.transfer,
        serverPubkeyHex: this.serverPubkeyHex,
        serverId: this.serverId,
      });
      return;
    }

    const existing = this.externalSessionsByKey.get(clientId);
    if (existing) {
      this.incrementRuntimeCounter("helloResumed");
      if (existing.externalDisconnectCleanupTimeout) {
        clearTimeout(existing.externalDisconnectCleanupTimeout);
        existing.externalDisconnectCleanupTimeout = null;
      }
      const newAppVersion = message.appVersion ?? null;
      if (newAppVersion && newAppVersion !== existing.appVersion) {
        existing.appVersion = newAppVersion;
        existing.session.updateAppVersion(newAppVersion);
      }
      existing.sockets.add(ws);
      this.sessions.set(ws, existing);
      this.sendToClient(ws, this.createServerInfoMessage());
      existing.connectionLogger.trace(
        {
          clientId,
          resumed: true,
          totalSessions: this.sessions.size,
        },
        "Client connected via hello",
      );
      return;
    }

    const connectionLogger = pending.connectionLogger.child({ clientId });
    this.incrementRuntimeCounter("helloNew");
    const connection = this.createSessionConnection({
      ws,
      clientId,
      appVersion: message.appVersion ?? null,
      connectionLogger,
      peerPublicKeyB64: pending.peerPublicKeyB64,
      userId: message.userId,
      username: message.username,
      wsToken: message.wsToken,
      isLoopback: pending.isLoopback,
    });
    this.sessions.set(ws, connection);
    this.externalSessionsByKey.set(clientId, connection);
    this.sendToClient(ws, this.createServerInfoMessage());
    // Push the initial agent snapshot so the client UI can paint attention
    // state before its own `fetch_agents_request` round-trip lands. The
    // regular fetch still runs in parallel to establish the subscription.
    void connection.session.sendInitialState();
    connection.connectionLogger.trace(
      {
        clientId,
        resumed: false,
        totalSessions: this.sessions.size,
      },
      "Client connected via hello",
    );
  }

  private buildServerInfoStatusPayload(): ServerInfoStatusPayload {
    const rawIcon = this.daemonConfigStore.get().identity?.icon;
    const daemonIcon = typeof rawIcon === "string" && rawIcon.length > 0 ? rawIcon : null;
    return {
      status: "server_info",
      serverId: this.serverId,
      hostname: getHostname(),
      version: this.daemonVersion,
      daemonIcon,
      ...(this.serverCapabilities ? { capabilities: this.serverCapabilities } : {}),
      features: {
        // COMPAT(providersSnapshot): keep optional until all clients rely on snapshot flow.
        providersSnapshot: true,
      },
    };
  }

  private createServerInfoMessage(): WSOutboundMessage {
    return {
      type: "session",
      message: {
        type: "status",
        payload: this.buildServerInfoStatusPayload(),
      },
    };
  }

  private createDaemonConfigChangedMessage(config: MutableDaemonConfig): WSOutboundMessage {
    return wrapSessionMessage({
      type: "status",
      payload: {
        status: "daemon_config_changed",
        config,
      },
    });
  }

  private broadcastCapabilitiesUpdate(): void {
    this.broadcast(this.createServerInfoMessage());
  }

  private broadcastDaemonConfigChanged(config: MutableDaemonConfig): void {
    this.broadcast(this.createDaemonConfigChangedMessage(config));
  }

  private bindSocketHandlers(ws: WebSocketLike): void {
    ws.on("message", (data) => {
      void this.handleRawMessage(ws, data);
    });

    ws.on("close", async (code: number, reason: unknown) => {
      await this.detachSocket(ws, {
        code: typeof code === "number" ? code : undefined,
        reason,
      });
    });

    ws.on("error", async (error) => {
      const err = error instanceof Error ? error : new Error(String(error));
      const active = this.sessions.get(ws);
      const pending = this.pendingConnections.get(ws);
      const log = active?.connectionLogger ?? pending?.connectionLogger ?? this.logger;
      log.error({ err }, "Client error");
      await this.detachSocket(ws, { error: err });
    });
  }

  public resolveVoiceSpeakHandler(callerAgentId: string): VoiceSpeakHandler | null {
    return this.voiceSpeakHandlers.get(callerAgentId) ?? null;
  }

  public resolveVoiceCallerContext(callerAgentId: string): VoiceCallerContext | null {
    return this.voiceCallerContexts.get(callerAgentId) ?? null;
  }

  private async detachSocket(
    ws: WebSocketLike,
    details: {
      code?: number;
      reason?: unknown;
      error?: Error;
    },
  ): Promise<void> {
    const pending = this.clearPendingConnection(ws);
    if (pending) {
      this.incrementRuntimeCounter("pendingDisconnected");
      pending.connectionLogger.trace(
        {
          code: details.code,
          reason: stringifyCloseReason(details.reason),
        },
        "Pending client disconnected",
      );
      return;
    }

    // tcp-forward connections don't have a Session — clean up the byte pump
    // directly and return; the rest of detachSocket targets session bookkeeping.
    if (this.tcpForwardManager.handleWsClose(ws)) {
      this.incrementRuntimeCounter("tcpForwardDisconnected");
      return;
    }

    // host-exec connections also have no Session — free the spawned child if the
    // WS drops before the command finishes.
    if (this.hostExecManager.handleWsClose(ws)) {
      this.incrementRuntimeCounter("hostExecDisconnected");
      return;
    }

    // host-transfer connections also have no Session — abort the in-flight
    // file (and drop any partial) if the WS drops before completion.
    if (this.hostTransferManager.handleWsClose(ws)) {
      this.incrementRuntimeCounter("hostTransferDisconnected");
      return;
    }

    const connection = this.sessions.get(ws);
    if (!connection) {
      return;
    }

    this.sessions.delete(ws);
    connection.sockets.delete(ws);

    if (connection.sockets.size === 0) {
      this.incrementRuntimeCounter("sessionDisconnectedWaitingReconnect");
      if (connection.externalDisconnectCleanupTimeout) {
        clearTimeout(connection.externalDisconnectCleanupTimeout);
      }
      const timeout = setTimeout(() => {
        if (connection.externalDisconnectCleanupTimeout !== timeout) {
          return;
        }
        connection.externalDisconnectCleanupTimeout = null;
        void this.cleanupConnection(connection, "Client disconnected (grace timeout)");
      }, EXTERNAL_SESSION_DISCONNECT_GRACE_MS);
      connection.externalDisconnectCleanupTimeout = timeout;

      connection.connectionLogger.trace(
        {
          clientId: connection.clientId,
          code: details.code,
          reason: stringifyCloseReason(details.reason),
          reconnectGraceMs: EXTERNAL_SESSION_DISCONNECT_GRACE_MS,
        },
        "Client disconnected; waiting for reconnect",
      );
      return;
    }

    if (connection.sockets.size > 0) {
      this.incrementRuntimeCounter("sessionSocketDisconnectedAttached");
      connection.connectionLogger.trace(
        {
          clientId: connection.clientId,
          remainingSockets: connection.sockets.size,
          code: details.code,
          reason: stringifyCloseReason(details.reason),
        },
        "Client socket disconnected; session remains attached",
      );
      return;
    }

    await this.cleanupConnection(connection, "Client disconnected");
  }

  private async cleanupConnection(
    connection: SessionConnection,
    logMessage: string,
  ): Promise<void> {
    this.incrementRuntimeCounter("sessionCleanup");
    if (connection.externalDisconnectCleanupTimeout) {
      clearTimeout(connection.externalDisconnectCleanupTimeout);
      connection.externalDisconnectCleanupTimeout = null;
    }

    for (const socket of connection.sockets) {
      this.sessions.delete(socket);
    }
    connection.sockets.clear();
    const existing = this.externalSessionsByKey.get(connection.clientId);
    if (existing === connection) {
      this.externalSessionsByKey.delete(connection.clientId);
    }

    connection.connectionLogger.trace(
      { clientId: connection.clientId, totalSessions: this.sessions.size },
      logMessage,
    );
    await connection.session.cleanup();
  }

  private async handleRawMessage(
    ws: WebSocketLike,
    data: Buffer | ArrayBuffer | Buffer[] | string,
  ): Promise<void> {
    const activeConnection = this.sessions.get(ws);
    const pendingConnection = this.pendingConnections.get(ws);
    const log =
      activeConnection?.connectionLogger ?? pendingConnection?.connectionLogger ?? this.logger;

    // Cross-host SSH (tcp-forward) sockets carry raw SSH bytes after the
    // hello. They have NO Session and MUST NOT enter the terminal-stream or
    // JSON-RPC dispatch paths. Route binary directly to the manager; ignore
    // any stray JSON (the dialer is not supposed to send any).
    if (this.tcpForwardManager.isTcpForward(ws)) {
      try {
        const buffer = bufferFromWsData(data);
        // Copy into a fresh ArrayBuffer — Node's `buffer.buffer` is typed as
        // ArrayBufferLike (includes SharedArrayBuffer), so a direct slice isn't
        // assignable to the manager's `ArrayBuffer` parameter.
        const ab = new ArrayBuffer(buffer.byteLength);
        new Uint8Array(ab).set(buffer);
        this.tcpForwardManager.routeBinary(ws, ab);
      } catch (err) {
        log.warn({ err }, "tcp_forward_route_failed");
      }
      return;
    }

    // host-exec sockets have no inbound data leg — the command arrived in the
    // hello. Drop any stray frame so it never enters session dispatch.
    if (this.hostExecManager.isHostExec(ws)) {
      return;
    }

    // host-transfer sockets DO have an inbound leg: acks (get) or chunks/done
    // (put). Route them straight to the manager, bypassing session dispatch.
    if (this.hostTransferManager.isTransfer(ws)) {
      try {
        const buffer = bufferFromWsData(data);
        this.hostTransferManager.routeFrame(ws, buffer);
      } catch (err) {
        log.warn({ err }, "host_transfer_route_failed");
      }
      return;
    }

    try {
      const buffer = bufferFromWsData(data);
      const asBytes = asUint8Array(buffer);
      if (asBytes) {
        const frame = decodeTerminalStreamFrame(asBytes);
        if (frame) {
          if (!activeConnection) {
            this.incrementRuntimeCounter("binaryBeforeHelloRejected");
            log.warn("Rejected binary frame before hello");
            this.clearPendingConnection(ws);
            try {
              ws.close(WS_CLOSE_INVALID_HELLO, "Session message before hello");
            } catch {
              // ignore close errors
            }
            return;
          }
          activeConnection.session.handleBinaryFrame(frame);
          return;
        }
      }
      const parsed = JSON.parse(buffer.toString());
      const parsedMessage = WSInboundMessageSchema.safeParse(parsed);
      if (!parsedMessage.success) {
        this.incrementRuntimeCounter("validationFailed");
        if (pendingConnection) {
          pendingConnection.connectionLogger.warn(
            {
              error: parsedMessage.error.message,
            },
            "Rejected pending message before hello",
          );
          this.clearPendingConnection(ws);
          try {
            ws.close(WS_CLOSE_INVALID_HELLO, "Invalid hello");
          } catch {
            // ignore close errors
          }
          return;
        }

        const requestInfo = extractRequestInfoFromUnknownWsInbound(parsed);
        const isUnknownSchema =
          requestInfo?.requestId != null &&
          typeof parsed === "object" &&
          parsed != null &&
          "type" in parsed &&
          (parsed as { type?: unknown }).type === "session";

        log.warn(
          {
            clientId: activeConnection?.clientId,
            requestId: requestInfo?.requestId,
            requestType: requestInfo?.requestType,
            error: parsedMessage.error.message,
          },
          "WS inbound message validation failed",
        );

        if (requestInfo) {
          this.sendToClient(
            ws,
            wrapSessionMessage({
              type: "rpc_error",
              payload: {
                requestId: requestInfo.requestId,
                requestType: requestInfo.requestType,
                error: isUnknownSchema ? "Unknown request schema" : "Invalid message",
                code: isUnknownSchema ? "unknown_schema" : "invalid_message",
              },
            }),
          );
          return;
        }

        const errorMessage = `Invalid message: ${parsedMessage.error.message}`;
        this.sendToClient(
          ws,
          wrapSessionMessage({
            type: "status",
            payload: {
              status: "error",
              message: errorMessage,
            },
          }),
        );
        return;
      }

      const message = parsedMessage.data;
      this.recordInboundMessageType(message.type);

      if (message.type === "ping") {
        this.sendToClient(ws, { type: "pong" });
        return;
      }

      if (message.type === "recording_state") {
        return;
      }

      if (pendingConnection) {
        if (message.type === "hello") {
          this.handleHello({
            ws,
            message,
            pending: pendingConnection,
          });
          return;
        }

        pendingConnection.connectionLogger.warn(
          {
            messageType: message.type,
          },
          "Rejected pending message before hello",
        );
        this.incrementRuntimeCounter("pendingMessageRejectedBeforeHello");
        this.clearPendingConnection(ws);
        try {
          ws.close(WS_CLOSE_INVALID_HELLO, "Session message before hello");
        } catch {
          // ignore close errors
        }
        return;
      }

      if (!activeConnection) {
        this.incrementRuntimeCounter("missingConnectionForMessage");
        this.logger.error("No connection found for websocket");
        return;
      }

      if (message.type === "hello") {
        this.incrementRuntimeCounter("unexpectedHelloOnActiveConnection");
        activeConnection.connectionLogger.warn("Received hello on active connection");
        try {
          ws.close(WS_CLOSE_INVALID_HELLO, "Unexpected hello");
        } catch {
          // ignore close errors
        }
        return;
      }

      if (message.type === "session") {
        this.recordInboundSessionRequestType(message.message.type);
        const startMs = performance.now();
        await activeConnection.session.handleMessage(message.message);
        const durationMs = performance.now() - startMs;
        this.recordRequestLatency(message.message.type, durationMs);

        if (durationMs >= SLOW_REQUEST_THRESHOLD_MS) {
          activeConnection.connectionLogger.warn(
            {
              requestType: message.message.type,
              durationMs: Math.round(durationMs),
              inflightRequests: activeConnection.session.getRuntimeMetrics().inflightRequests,
            },
            "ws_slow_request",
          );
        }
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      let rawPayload: string | null = null;
      let parsedPayload: unknown = null;

      try {
        const buffer = bufferFromWsData(data);
        rawPayload = buffer.toString();
        parsedPayload = JSON.parse(rawPayload);
      } catch (payloadError) {
        rawPayload = rawPayload ?? "<unreadable>";
        parsedPayload = parsedPayload ?? rawPayload;
        const payloadErr =
          payloadError instanceof Error ? payloadError : new Error(String(payloadError));
        this.logger.error({ err: payloadErr }, "Failed to decode raw payload");
      }

      const trimmedRawPayload =
        typeof rawPayload === "string" && rawPayload.length > 2000
          ? `${rawPayload.slice(0, 2000)}... (truncated)`
          : rawPayload;

      log.error(
        {
          err,
          rawPayload: trimmedRawPayload,
          parsedPayload,
        },
        "Failed to parse/handle message",
      );

      if (this.pendingConnections.has(ws)) {
        this.clearPendingConnection(ws);
        try {
          ws.close(WS_CLOSE_INVALID_HELLO, "Invalid hello");
        } catch {
          // ignore close errors
        }
        return;
      }

      const requestInfo = extractRequestInfoFromUnknownWsInbound(parsedPayload);
      if (requestInfo) {
        this.sendToClient(
          ws,
          wrapSessionMessage({
            type: "rpc_error",
            payload: {
              requestId: requestInfo.requestId,
              requestType: requestInfo.requestType,
              error: "Invalid message",
              code: "invalid_message",
            },
          }),
        );
        return;
      }

      this.sendToClient(
        ws,
        wrapSessionMessage({
          type: "status",
          payload: {
            status: "error",
            message: `Invalid message: ${err.message}`,
          },
        }),
      );
    }
  }

  private readonly ACTIVITY_THRESHOLD_MS = 120_000;

  private incrementRuntimeCounter(counter: keyof WebSocketRuntimeCounters): void {
    this.runtimeCounters[counter] += 1;
  }

  private incrementCount(map: Map<string, number>, key: string): void {
    map.set(key, (map.get(key) ?? 0) + 1);
  }

  private recordInboundMessageType(type: string): void {
    this.incrementCount(this.inboundMessageCounts, type);
  }

  private recordInboundSessionRequestType(type: string): void {
    this.incrementCount(this.inboundSessionRequestCounts, type);
  }

  private recordOutboundMessage(message: WSOutboundMessage, ws: WebSocketLike): void {
    if (message.type !== "session") {
      this.incrementCount(this.outboundMessageCounts, message.type);
      this.recordBufferedAmount(ws);
      return;
    }

    this.incrementCount(this.outboundMessageCounts, "session_message");
    this.incrementCount(this.outboundSessionMessageCounts, message.message.type);

    if (message.message.type === "agent_stream") {
      this.recordOutboundAgentStreamMessage(message.message.payload);
    }

    this.recordBufferedAmount(ws);
  }

  private recordOutboundAgentStreamMessage(
    payload: Extract<SessionOutboundMessage, { type: "agent_stream" }>["payload"],
  ): void {
    const { agentId, event } = payload;
    const eventType = event.type === "timeline" ? `timeline:${event.item.type}` : event.type;
    this.incrementCount(this.outboundAgentStreamCounts, eventType);
    this.incrementCount(this.outboundAgentStreamByAgentCounts, agentId);
  }

  private recordOutboundBinaryFrame(ws: WebSocketLike): void {
    this.incrementCount(this.outboundBinaryFrameCounts, "binary");
    this.recordBufferedAmount(ws);
  }

  private recordBufferedAmount(ws: WebSocketLike): void {
    if (typeof ws.bufferedAmount !== "number") {
      return;
    }
    this.bufferedAmountSamples.push(ws.bufferedAmount);
  }

  private recordRequestLatency(type: string, durationMs: number): void {
    let latencies = this.requestLatencies.get(type);
    if (!latencies) {
      latencies = [];
      this.requestLatencies.set(type, latencies);
    }
    latencies.push(durationMs);
  }

  private getTopCounts(map: Map<string, number>, limit: number): Array<[string, number]> {
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit);
  }

  private computeLatencyStats(): Array<{
    type: string;
    count: number;
    minMs: number;
    maxMs: number;
    p50Ms: number;
    totalMs: number;
  }> {
    const stats: Array<{
      type: string;
      count: number;
      minMs: number;
      maxMs: number;
      p50Ms: number;
      totalMs: number;
    }> = [];
    for (const [type, latencies] of this.requestLatencies) {
      if (latencies.length === 0) continue;
      latencies.sort((a, b) => a - b);
      const count = latencies.length;
      const minMs = Math.round(latencies[0]!);
      const maxMs = Math.round(latencies[count - 1]!);
      const p50Ms = Math.round(latencies[Math.floor(count / 2)]!);
      const totalMs = Math.round(latencies.reduce((sum, v) => sum + v, 0));
      stats.push({ type, count, minMs, maxMs, p50Ms, totalMs });
    }
    stats.sort((a, b) => b.totalMs - a.totalMs);
    return stats.slice(0, 15);
  }

  private computeBufferedAmountStats(): {
    p95: number;
    max: number;
  } {
    if (this.bufferedAmountSamples.length === 0) {
      return { p95: 0, max: 0 };
    }

    const samples = [...this.bufferedAmountSamples].sort((a, b) => a - b);
    const p95Index = Math.ceil(samples.length * 0.95) - 1;
    return {
      p95: samples[p95Index] ?? 0,
      max: samples[samples.length - 1] ?? 0,
    };
  }

  private collectSessionRuntimeMetrics(): WebSocketRuntimeMetrics {
    const uniqueConnections = new Set<SessionConnection>(this.externalSessionsByKey.values());
    let terminalDirectorySubscriptionCount = 0;
    let terminalSubscriptionCount = 0;
    let inflightRequests = 0;
    let peakInflightRequests = 0;

    for (const connection of uniqueConnections) {
      const sessionMetrics = connection.session.getRuntimeMetrics();
      terminalDirectorySubscriptionCount += sessionMetrics.terminalDirectorySubscriptionCount;
      terminalSubscriptionCount += sessionMetrics.terminalSubscriptionCount;
      inflightRequests += sessionMetrics.inflightRequests;
      peakInflightRequests = Math.max(peakInflightRequests, sessionMetrics.peakInflightRequests);
      connection.session.resetPeakInflight();
    }

    return {
      ...this.checkoutDiffManager.getMetrics(),
      terminalDirectorySubscriptionCount,
      terminalSubscriptionCount,
      inflightRequests,
      peakInflightRequests,
    };
  }

  private flushRuntimeMetrics(options?: { final?: boolean }): void {
    const now = Date.now();
    const windowMs = Math.max(0, now - this.runtimeWindowStartedAt);
    const activeConnections = new Set<SessionConnection>(this.sessions.values()).size;
    const activeSockets = this.sessions.size;
    const pendingConnections = this.pendingConnections.size;
    const reconnectGraceSessions = [...this.externalSessionsByKey.values()].filter(
      (connection) =>
        connection.sockets.size === 0 && connection.externalDisconnectCleanupTimeout !== null,
    ).length;
    const sessionMetrics = this.collectSessionRuntimeMetrics();
    const latencyStats = this.computeLatencyStats();
    const bufferedAmountStats = this.computeBufferedAmountStats();
    const agentSnapshot = this.agentManager.getMetricsSnapshot();

    this.logger.info(
      {
        windowMs,
        final: Boolean(options?.final),
        sessions: {
          activeConnections,
          externalSessionKeys: this.externalSessionsByKey.size,
          reconnectGraceSessions,
        },
        sockets: {
          activeSockets,
          pendingConnections,
        },
        counters: { ...this.runtimeCounters },
        inboundMessageTypesTop: this.getTopCounts(this.inboundMessageCounts, 12),
        inboundSessionRequestTypesTop: this.getTopCounts(this.inboundSessionRequestCounts, 20),
        outboundMessageTypesTop: this.getTopCounts(this.outboundMessageCounts, 12),
        outboundSessionMessageTypesTop: this.getTopCounts(this.outboundSessionMessageCounts, 20),
        outboundAgentStreamTypesTop: this.getTopCounts(this.outboundAgentStreamCounts, 20),
        outboundAgentStreamAgentsTop: this.getTopCounts(this.outboundAgentStreamByAgentCounts, 20),
        outboundBinaryFrameTypesTop: this.getTopCounts(this.outboundBinaryFrameCounts, 12),
        bufferedAmount: bufferedAmountStats,
        runtime: sessionMetrics,
        latency: latencyStats,
        agents: agentSnapshot,
      },
      "ws_runtime_metrics",
    );

    for (const counter of Object.keys(this.runtimeCounters) as Array<
      keyof WebSocketRuntimeCounters
    >) {
      this.runtimeCounters[counter] = 0;
    }
    this.inboundMessageCounts.clear();
    this.inboundSessionRequestCounts.clear();
    this.outboundMessageCounts.clear();
    this.outboundSessionMessageCounts.clear();
    this.outboundAgentStreamCounts.clear();
    this.outboundAgentStreamByAgentCounts.clear();
    this.outboundBinaryFrameCounts.clear();
    this.bufferedAmountSamples.length = 0;
    this.requestLatencies.clear();
    this.runtimeWindowStartedAt = now;
  }

  private getClientActivityState(session: Session): ClientAttentionState {
    const activity = session.getClientActivity();
    if (!activity) {
      return { deviceType: null, focusedAgentId: null, isStale: true, appVisible: false };
    }
    const now = Date.now();
    const ageMs = now - activity.lastActivityAt.getTime();
    const isStale = ageMs >= this.ACTIVITY_THRESHOLD_MS;
    return {
      deviceType: activity.deviceType,
      focusedAgentId: activity.focusedAgentId,
      isStale,
      appVisible: activity.appVisible,
    };
  }

  private async broadcastAgentAttention(params: {
    agentId: string;
    provider: AgentProvider;
    reason: "finished" | "error" | "permission";
  }): Promise<void> {
    const clientEntries: Array<{
      ws: WebSocketLike;
      state: ClientAttentionState;
    }> = [];

    for (const [ws, connection] of this.sessions) {
      clientEntries.push({
        ws,
        state: this.getClientActivityState(connection.session),
      });
    }

    const allStates = clientEntries.map((e) => e.state);
    const agent = this.agentManager.getAgentSnapshot(params.agentId);
    const assistantMessage = await this.agentManager.getLastAssistantMessage(params.agentId);
    const notification = buildAgentAttentionNotificationPayload({
      reason: params.reason,
      serverId: this.serverId,
      agentId: params.agentId,
      assistantMessage,
      permissionRequest: agent ? findLatestPermissionRequest(agent.pendingPermissions) : null,
    });

    // Push is only a fallback when the user is away from desktop/web.
    // Also suppress push if they're actively using the mobile app.
    const shouldSendPush = computeShouldSendPush({
      reason: params.reason,
      allClientStates: allStates,
    });

    if (shouldSendPush) {
      const eventId = reasonToEventId(params.reason);
      // Scope fan-out to the agent's owner + explicit shares. Without this,
      // every subscribed device on a shared host receives every agent's
      // attention event. Falls back to the legacy global fan-out when the
      // agent is missing or unscoped (`ownerUserId === null`).
      const scope = agent ? { ownerUserId: agent.ownerUserId } : null;
      const tokens = scope
        ? this.pushTokenStore.getTokensForEventAndAgent(eventId, scope)
        : this.pushTokenStore.getTokensForEvent(eventId);
      const webSubs = scope
        ? this.webPushStore.getSubsForEventAndAgent(eventId, scope)
        : this.webPushStore.getSubsForEvent(eventId);
      this.logger.info(
        {
          eventId,
          agentId: params.agentId,
          agentOwnerUserId: agent?.ownerUserId ?? null,
          expoTokenCount: tokens.length,
          webPushSubCount: webSubs.length,
          totalExpo: this.pushTokenStore.getAllTokens().length,
          totalWeb: this.webPushStore.getAll().length,
        },
        "Sending push notification (filtered by per-device prefs + agent owner)",
      );
      if (tokens.length > 0) {
        void this.pushService.sendPush(tokens, notification);
      }
      if (webSubs.length > 0) {
        void this.webPushService.sendPush(webSubs, {
          title: notification.title,
          body: notification.body,
          data: { ...notification.data, type: params.reason },
        });
      }
    }

    for (const { ws, state } of clientEntries) {
      // Gate the in-band attention event on the same visibility predicate
      // used everywhere else — otherwise a user sharing the host receives
      // attention frames for agents they can't see. Sessions always carry
      // a resolved owner (websocket-server rejects unresolvable peers),
      // and agents always carry a non-null ownerUserId post-migration —
      // so this filter is always live.
      const connection = this.sessions.get(ws);
      if (connection) {
        const requesterUserId = connection.session.getOwnerUserId();
        if (!this.agentManager.canUserAccessAgentById(params.agentId, requesterUserId)) {
          continue;
        }
      }

      const shouldNotify = computeShouldNotifyClient({
        clientState: state,
        allClientStates: allStates,
        agentId: params.agentId,
      });

      const message = wrapSessionMessage({
        type: "agent_stream",
        payload: {
          agentId: params.agentId,
          event: {
            type: "attention_required",
            provider: params.provider,
            reason: params.reason,
            timestamp: new Date().toISOString(),
            shouldNotify,
            notification,
          },
          timestamp: new Date().toISOString(),
        },
      });

      this.sendToClient(ws, message);
    }
  }
}

type SocketRequestMetadata = {
  host?: string;
  origin?: string;
  userAgent?: string;
  remoteAddress?: string;
};

function extractSocketRequestMetadata(request: unknown): SocketRequestMetadata {
  if (!request || typeof request !== "object") {
    return {};
  }

  const record = request as {
    headers?: {
      host?: unknown;
      origin?: unknown;
      "user-agent"?: unknown;
    };
    url?: unknown;
    socket?: {
      remoteAddress?: unknown;
    };
  };

  const host = typeof record.headers?.host === "string" ? record.headers.host : undefined;
  const origin = typeof record.headers?.origin === "string" ? record.headers.origin : undefined;
  const userAgent =
    typeof record.headers?.["user-agent"] === "string" ? record.headers["user-agent"] : undefined;
  const remoteAddress =
    typeof record.socket?.remoteAddress === "string" ? record.socket.remoteAddress : undefined;

  return {
    ...(host ? { host } : {}),
    ...(origin ? { origin } : {}),
    ...(userAgent ? { userAgent } : {}),
    ...(remoteAddress ? { remoteAddress } : {}),
  };
}

function timingSafeTokenEqual(a: string, b: string): boolean {
  const aBuf = Buffer.from(a);
  const bBuf = Buffer.from(b);
  if (aBuf.length !== bBuf.length) {
    return false;
  }
  return timingSafeEqual(aBuf, bBuf);
}

function isLoopbackAddress(address: string | null): boolean {
  if (!address) {
    return false;
  }
  return address === "127.0.0.1" || address === "::1" || address === "::ffff:127.0.0.1";
}

function stringifyCloseReason(reason: unknown): string | null {
  if (typeof reason === "string") {
    return reason.length > 0 ? reason : null;
  }
  if (Buffer.isBuffer(reason)) {
    const text = reason.toString();
    return text.length > 0 ? text : null;
  }
  if (reason == null) {
    return null;
  }
  const text = String(reason);
  return text.length > 0 ? text : null;
}

function extractRequestInfoFromUnknownWsInbound(
  payload: unknown,
): { requestId: string; requestType?: string } | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const record = payload as {
    type?: unknown;
    requestId?: unknown;
    message?: unknown;
  };

  // Session-wrapped messages
  if (record.type === "session" && record.message && typeof record.message === "object") {
    const msg = record.message as { requestId?: unknown; type?: unknown };
    if (typeof msg.requestId === "string") {
      return {
        requestId: msg.requestId,
        ...(typeof msg.type === "string" ? { requestType: msg.type } : {}),
      };
    }
  }

  // Non-session messages (future-proof)
  if (typeof record.requestId === "string") {
    return {
      requestId: record.requestId,
      ...(typeof record.type === "string" ? { requestType: record.type } : {}),
    };
  }

  return null;
}
